import { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import { WORKFLOW_ICONS } from "@/utils/consts";
import { getSpotFileUrl } from "@/utils/generic";
import { cn } from "@/lib/utils";
import { Spinner } from "./Spinner";

export const SIZE_MAP = {
  xs: {
    padding: "0.125rem",
    icon: "0.5rem",
    radius: "0.25rem",
    image: "0.75rem",
  },
  xsm: {
    padding: "0.125rem",
    icon: "0.75rem",
    radius: "0.25rem",
    image: "1rem",
  },
  smd: {
    padding: "0.1875rem",
    icon: "0.875rem",
    radius: "0.25rem",
    image: "1.25rem",
  },
  sm: { padding: "0.25rem", icon: "1rem", radius: "0.25rem", image: "1.5rem" },
  md: {
    padding: "0.25rem",
    icon: "1.25rem",
    radius: "0.25rem",
    image: "1.75rem",
  },
  mdlg: {
    padding: "0.5rem",
    icon: "1.25rem",
    radius: "0.25rem",
    image: "2rem",
  },
  lg: { padding: "0.5rem", icon: "1.5rem", radius: "0.5rem", image: "2.5rem" },
  xl: { padding: "0.5rem", icon: "2rem", radius: "0.5rem", image: "3.375rem" },
  xxl: { padding: "0.5rem", icon: "3rem", radius: "1rem", image: "5rem" },
};

interface NoxusIconProps {
  image?: string;
  icon?: string;
  size?: keyof typeof SIZE_MAP;
  color?: string;
  borderColor?: string;
  showBorder?: boolean;
  showBorderOnHover?: boolean;
  isPublic?: boolean;
  showLoader?: boolean;
}

export const NoxusIcon = ({
  image,
  icon,
  size = "sm",
  color,
  borderColor = "#4C4F56",
  showBorder,
  showBorderOnHover,
  isPublic = false,
  showLoader,
}: NoxusIconProps) => {
  const isNoxusImage = icon?.startsWith("spot:");
  const [loadingImage, setLoadingImage] = useState(!!showLoader);

  const iconData = useMemo(() => {
    if (isNoxusImage || image) return undefined;

    return icon && icon in WORKFLOW_ICONS
      ? WORKFLOW_ICONS[icon as keyof typeof WORKFLOW_ICONS]
      : WORKFLOW_ICONS.workflow;
  }, [icon, image, isNoxusImage]);

  const img = isNoxusImage
    ? getSpotFileUrl(icon!, false, isPublic)
    : iconData?.image || image;

  const borderStyle = showBorder
    ? `0px 0px 0px 1px ${iconData?.borderColor || borderColor}`
    : "unset";
  const hoverBorderStyle = showBorderOnHover
    ? `0px 0px 0px 1px ${iconData?.borderColor || borderColor}`
    : "unset";

  return (
    <div
      className={cn(
        "flex items-center justify-center shrink-0 box-border overflow-hidden border border-black/10 dark:border-white/15"
      )}
      style={{
        borderRadius: SIZE_MAP[size].radius,
        width: SIZE_MAP[size].image,
        height: SIZE_MAP[size].image,
        backgroundColor: iconData?.color ?? color,
        boxShadow: borderStyle,
      }}
      onMouseEnter={(e) => {
        if (showBorderOnHover) {
          e.currentTarget.style.boxShadow = hoverBorderStyle;
        }
      }}
      onMouseLeave={(e) => {
        if (showBorderOnHover) {
          e.currentTarget.style.boxShadow = borderStyle;
        }
      }}
    >
      {img ? (
        <div
          className="relative"
          style={{
            height: isNoxusImage ? "inherit" : SIZE_MAP[size].icon,
            width: isNoxusImage ? "inherit" : SIZE_MAP[size].icon,
          }}
        >
          {loadingImage && (
            <div className="absolute bg-primary opacity-80 inset-0 flex items-center justify-center">
              <Spinner className="text-white size-4" />
            </div>
          )}
          <Image
            objectFit="cover"
            src={img}
            alt="Noxus icon"
            fill
            onLoad={() => setLoadingImage(false)}
            onError={() => setLoadingImage(false)}
          />
        </div>
      ) : iconData?.icon ? (
        (() => {
          const IconComponent = iconData.icon;
          return (
            <IconComponent
              color={iconData.borderColor}
              strokeWidth="1.25px"
              style={{
                width: SIZE_MAP[size].icon,
                height: SIZE_MAP[size].icon,
              }}
            />
          );
        })()
      ) : null}
    </div>
  );
};
